import { AnalyticsRepository } from '../../domain/repositories/analytics.repository';
import {
  AnalyticsDashboard,
  RawProductView,
  RawSearchLog,
  RawProductInquiry,
} from '../../domain/entities/analytics.entity';
import { IHttpClient } from '../../application/interfaces/http-client.interface';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  total?: number;
  message?: string;
}

export class HttpAnalyticsRepository implements AnalyticsRepository {
  private recentViews = new Map<string, number>();
  private recentSearches = new Map<string, number>();
  private recentInquiries = new Map<string, number>();

  constructor(private readonly httpClient: IHttpClient) {}

  public async trackProductView(productId: string): Promise<void> {
    try {
      if (!productId) return;
      const now = Date.now();
      const last = this.recentViews.get(productId) || 0;
      if (now - last < 5000) {
        return; // Evita duplicar clics en menos de 5 segundos
      }
      this.recentViews.set(productId, now);
      await this.httpClient.post('/analytics/views', { productId });
    } catch {
      // Silencioso por diseño para no interrumpir la navegación del usuario
    }
  }

  public async trackSearch(query: string, resultsCount: number = 0): Promise<void> {
    try {
      const trimmed = query?.trim().toLowerCase();
      if (!trimmed || trimmed.length < 2) return;
      const now = Date.now();
      const last = this.recentSearches.get(trimmed) || 0;
      if (now - last < 5000) {
        return;
      }
      this.recentSearches.set(trimmed, now);
      await this.httpClient.post('/analytics/searches', { query: trimmed, resultsCount });
    } catch {
      // Silencioso por diseño
    }
  }

  public async trackProductInquiry(
    productId: string,
    origin: 'catalog_card' | 'product_detail' = 'catalog_card'
  ): Promise<void> {
    try {
      if (!productId) return;
      const key = `${productId}_${origin}`;
      const now = Date.now();
      const last = this.recentInquiries.get(key) || 0;
      if (now - last < 5000) {
        return;
      }
      this.recentInquiries.set(key, now);
      await this.httpClient.post('/analytics/inquiries', { productId, origin });
    } catch {
      // Silencioso por diseño
    }
  }

  public async getDashboard(startDate?: string, endDate?: string): Promise<AnalyticsDashboard> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await this.httpClient.get<ApiResponse<AnalyticsDashboard>>(`/analytics/dashboard${query}`);

    if (!res.success || !res.data) {
      return {
        totalViews: 0,
        totalSearches: 0,
        totalInquiries: 0,
        conversionRate: 0,
        zeroResultSearchesCount: 0,
        topViewedProducts: [],
        topSearches: [],
        topInquiredProducts: [],
        dailyActivity: [],
      };
    }

    return res.data;
  }

  public async getProductViews(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<{ items: RawProductView[]; total: number }> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search?.trim()) params.append('search', search.trim());

    const res = await this.httpClient.get<ApiResponse<RawProductView[]>>(`/analytics/views?${params.toString()}`);
    return {
      items: res.data || [],
      total: res.total || 0,
    };
  }

  public async getSearchLogs(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<{ items: RawSearchLog[]; total: number }> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search?.trim()) params.append('search', search.trim());

    const res = await this.httpClient.get<ApiResponse<RawSearchLog[]>>(`/analytics/searches?${params.toString()}`);
    return {
      items: res.data || [],
      total: res.total || 0,
    };
  }

  public async getProductInquiries(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<{ items: RawProductInquiry[]; total: number }> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search?.trim()) params.append('search', search.trim());

    const res = await this.httpClient.get<ApiResponse<RawProductInquiry[]>>(`/analytics/inquiries?${params.toString()}`);
    return {
      items: res.data || [],
      total: res.total || 0,
    };
  }
}
